import { motionOk } from "../ui/kit";
import type { LevelParts } from "./levelParts";
import { MarkerKind, OffscreenMarkers, markerTargets } from "./offscreenMarkers";
import { appHeight } from "../ui/appSize";

/**
 * How much of the map the player sees: the band the HUD and the bottom panels leave (the camera frames the map in it),
 * the whole-map view (QoL C3), and the edge markers for what is off screen.
 */
export class LevelLayout {
  /** The HUD's and the bottom panels' covered heights (CSS px) as last framed; NaN before the first measurement. */
  insetTop = NaN;
  insetBottom = NaN;
  /** The bottom inset the camera frames the map within (the covered one, bar an unpicked hand dealt over the view). */
  private framedBottom = NaN;
  /** Border-box heights of the HUD panels, as the ResizeObserver last reported them. */
  private readonly panelHeights = new Map<Element, number>();
  private panelObserver: ResizeObserver | null = null;
  /** Edge markers for the portal, a boss and a threatened fortress while they are off screen (QoL C3). */
  private readonly markers: OffscreenMarkers;

  constructor(private readonly p: LevelParts) {
    this.markers = new OffscreenMarkers(p.uiRoot, (k) => this.showMarked(k));
  }

  /**
   * Tells the camera how much of the canvas the HUD and the card hand cover, so the map is framed between them.
   *
   * Runs after every sim change, i.e. every frame of a battle. Measuring with getBoundingClientRect there forced a
   * synchronous style + layout in the middle of the frame whenever the HUD text had just changed (the enemies-left
   * count, the countdown); the heights now come from a ResizeObserver, which the browser feeds from the layout it
   * does anyway, and the camera is only re-aimed when an inset actually moves.
   */
  update(): void {
    const p = this.p, info = p.towerInfo.root, hand = p.hand.root;
    const infoOpen = !info.classList.contains("hidden");
    const bottom = infoOpen ? this.covered(info) : !hand.classList.contains("hidden") ? this.covered(hand) : this.covered(p.hud.controls);
    p.hud.setSheetLift(infoOpen ? bottom : null);      // pause and speed ride on the tower sheet's top edge
    p.hud.setClusterLift(!infoOpen && !hand.classList.contains("hidden") ? bottom : null);
    const top = this.covered(p.hud.root);
    if (top === this.insetTop && bottom === this.insetBottom) return;
    this.insetTop = top; this.insetBottom = bottom;
    // The first measurement frames the new level at once; after that a panel changing size (the tray dealt, morphed
    // into the placement bar or put away, the tower sheet) eases the view to the new band instead of snapping it,
    // and a hand dealt with nothing picked leaves the view be (PlacementCamera.framingBottom).
    const handUp = !infoOpen && !hand.classList.contains("hidden") && !(p.sim.currentOffer?.hasPick ?? false);
    const framed = p.camFollow.framingBottom(p.sim, bottom, handUp, this.framedBottom);
    if (Number.isNaN(this.framedBottom)) p.rig.setInsets(top, framed);
    else p.rig.iso.easeInsets(top, framed);
    this.framedBottom = framed;
    // Picking a card morphs the tray into the shorter placement bar a layout later: the view was aimed for the old
    // tray, so aim again for the band that is really uncovered (the dead zone keeps this from twitching).
    p.camFollow.insetsChanged();
  }

  private covered(el: Element): number {
    // Measured directly only until the observer's first report (and always where there is no ResizeObserver).
    return this.panelHeights.get(el) ?? el.getBoundingClientRect().height;
  }

  /** Starts following the panels' sizes (once the HUD, the tray and the tower sheet exist). */
  watchPanels(): void {
    if (typeof ResizeObserver === "undefined") return;
    const p = this.p;
    this.panelObserver = new ResizeObserver((entries) => {
      for (const e of entries) this.panelHeights.set(e.target, e.borderBoxSize?.[0]?.blockSize ?? e.target.getBoundingClientRect().height);
      if (!p.disposed) this.update();
    });
    for (const el of [p.hud.root, p.hud.controls, p.hand.root, p.towerInfo.root]) this.panelObserver.observe(el, { box: "border-box" });
  }

  /** The whole map (QoL C3), or back from it (to `at`, or the view it left); the placement glide lets go of the view. */
  toggleWholeMap(at: { x: number; z: number } | null): void {
    const p = this.p;
    p.camFollow.take();
    p.rig.iso.toggleWholeMap(at);
    p.hud.setWholeMap(p.rig.iso.wholeMap);
    p.raise("mapMoved");
  }

  /** Once a frame: the edge markers, and the HUD's whole-map button when a pinch ended the whole map. */
  frame(): void {
    const p = this.p;
    this.updateMarkers();
    if (p.hud.wholeMapShown !== p.rig.iso.wholeMap) p.hud.setWholeMap(p.rig.iso.wholeMap);   // a pinch ended it
  }

  private updateMarkers(): void {
    const p = this.p;
    if (p.ended || p.disposed) { this.markers.hideAll(); return; }
    const band = { left: 0, top: this.insetTop, right: innerWidth, bottom: appHeight() - this.insetBottom };
    if (!(band.bottom > band.top)) { this.markers.hideAll(); return; }
    this.markers.update(markerTargets(p.sim), (x, y, z) => p.rig.iso.worldToViewport(x, y, z), band, p.hud.mapControlRects());
  }

  /** A tap on an edge marker glides the view to what it marks. */
  private showMarked(kind: MarkerKind): void {
    const p = this.p, t = markerTargets(p.sim)[kind];
    if (t === null) return;
    p.camFollow.take();
    if (p.rig.iso.wholeMap) this.toggleWholeMap(t);
    else if (motionOk()) p.rig.iso.glide(t.x, t.z); else p.rig.iso.focus(t.x, t.z);
    p.raise("mapMoved");
  }

  dispose(): void {
    this.panelObserver?.disconnect();
    this.markers.dispose();
  }
}
